<template>
  <div class="subscribe-view">
    <v-container>
      <!-- ─────────────────────────────────────────────────────────────
           1. COMPACT HERO - deliberately short so the plan cards below
           stay above the fold. Do not add tall content above the cards.
           ───────────────────────────────────────────────────────────── -->
      <div id="subscribe-top" class="subscribe-hero text-center">
        <div class="subscribe-eyebrow">
          {{ heroCopy.eyebrow }}
        </div>
        <h1 class="subscribe-title">
          {{ heroCopy.title }}
        </h1>
        <p class="subscribe-subtitle">
          {{ heroCopy.subtitle }}
        </p>
        <!-- The page's one trust strip: the app's own signals when it names
             them (SUBSCRIBE_TRUST_SIGNALS), otherwise the generic three. -->
        <div v-if="heroTrustItems.length" class="subscribe-trust-strip">
          <template v-for="(item, index) in heroTrustItems">
            <span v-if="index" :key="'dot' + index" class="trust-dot">·</span>
            <span :key="'item' + index" class="trust-item">
              <v-icon x-small :color="item.color">{{ item.icon }}</v-icon>
              {{ item.label }}
            </span>
          </template>
        </div>
        <p v-if="userName" class="subscribe-signed-in">
          Signed in as {{ userName }} -
          <a href="#" class="subscribe-text-link" @click.prevent="logout"
            >log out</a
          >
        </p>
      </div>

      <!-- A bounced renewal is fixed in Billing, not by picking a plan: the member's own plan card is
           disabled here, and another plan cannot replace a bound subscription. -->
      <v-alert v-if="paymentFailed" type="warning" text class="mb-4">
        Your last payment failed. Fix your payment method to keep your plan.
        <router-link to="/subscription" class="subscribe-text-link">Manage billing</router-link>
      </v-alert>

      <!-- ─────────────────────────────────────────────────────────────
           2. PLAN CARDS - the decision surface, kept above the fold.
           ───────────────────────────────────────────────────────────── -->
      <v-row id="plans" class="plans-row">
        <!-- Left: plan cards. Under force-paid the demo panel takes lg=3, so
             the cards get lg=9 and sit 3-per-row; without it (free tier
             visible, 4 plans, or an app with no demo video) they take the
             full width. -->
        <v-col cols="12" :lg="showDemoVideo ? 9 : 12">
          <v-row justify="center">
            <v-col
              v-for="(plan, index) in plans"
              :key="plan.id"
              cols="12"
              sm="6"
              :md="forcePaidPlans ? 4 : 3"
            >
              <subscription-plan-card
                :plan="plan"
                :current-plan="currentPlan"
                :tagline="tierTagline(plan)"
                :recommended="isRecommended(plan)"
                :recommended-label="recommendedLabel"
                :previous-tier-name="previousTierName(index)"
                @select-plan="startCheckout"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Right: demo video. Sits beside the cards on wide screens and
             stacks below them under lg so the cards never get cramped. -->
        <v-col v-if="showDemoVideo" cols="12" lg="3" class="d-flex">
          <div
            class="demo-video-panel"
            role="button"
            tabindex="0"
            @click="showVideoModal = true"
            @keydown.enter="showVideoModal = true"
          >
            <div class="demo-video-header">
              <div class="demo-video-eyebrow">
                <v-icon small color="primary">mdi-play-circle-outline</v-icon>
                New here?
              </div>
              <div class="demo-video-title">See {{ $config.productName }} in action</div>
              <div class="demo-video-sub">
                {{ demoVideo.subtitle }}
              </div>
            </div>

            <div class="demo-video-thumb">
              <img
                :src="demoVideo.poster"
                :alt="'Watch the ' + $config.productName + ' demo'"
                class="demo-video-img"
              />
              <div class="demo-video-overlay">
                <div class="demo-video-play-circle">
                  <v-icon size="30" color="white">mdi-play</v-icon>
                </div>
              </div>
            </div>

            <div class="demo-video-footer">
              <div class="demo-video-points-title">In the demo you'll see:</div>
              <div v-for="point in demoVideo.points" :key="point" class="demo-video-point">
                <v-icon small color="success">mdi-check-circle</v-icon>
                <span>{{ point }}</span>
              </div>
              <div class="demo-video-watch">
                <v-icon small color="primary">mdi-play</v-icon>
                Watch the demo
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Short legal line + scroll cue (keeps the long disclaimer for §7) -->
      <v-row>
        <v-col cols="12" class="text-center">
          <p class="caption grey--text mb-2">
            Prices in {{ ($config.currency || 'usd').toUpperCase() }}; taxes may apply at checkout. Subscriptions renew
            monthly until cancelled. {{ $config.USAGE_TERMS || 'Credits are usage units for processing time, not money or stored value, and reset monthly.' }}
          </p>
          <a
            href="#everything"
            class="subscribe-scroll-cue"
            @click.prevent="scrollToId('everything')"
          >
            <v-icon small color="primary">mdi-arrow-down</v-icon>
            {{ hasPlanMarketing ? "More details below" : "Everything you get & full plan comparison below" }}
          </a>
        </v-col>
      </v-row>

      <!-- ─────────────────────────────────────────────────────────────
           3. CAPABILITY SHOWCASE (below the fold). Reference-app copy
           ("the whole processing pipeline", agent/API access) describes
           the platform's job-processing shape, not necessarily this app -
           apps with PLAN_MARKETING already carry their real
           differentiation on the plan cards above, so this (and the
           comparison table) is reference-app-only content.
           ───────────────────────────────────────────────────────────── -->
      <div v-if="!hasPlanMarketing" class="subscribe-section">
        <div class="text-center mb-8">
          <h2 class="section-h2">Everything your plan unlocks</h2>
          <p class="section-sub">One subscription, the whole processing pipeline.</p>
        </div>
        <v-row>
          <v-col
            v-for="cap in capabilities"
            :key="cap.title"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="capability-card">
              <v-icon size="28" color="primary">{{ cap.icon }}</v-icon>
              <div class="capability-title">{{ cap.title }}</div>
              <div class="capability-desc">{{ cap.desc }}</div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           4. COMPARISON TABLE (below the fold)
           ───────────────────────────────────────────────────────────── -->
      <!-- The generic comparison rows (concurrency, API access, pack rates)
           describe the reference app; apps that supply PLAN_MARKETING have
           their full differentiation on the cards above. -->
      <div v-if="!hasPlanMarketing" class="subscribe-section">
        <div class="text-center mb-8">
          <h2 class="section-h2">Compare all plans</h2>
          <p class="section-sub">
            Every plan includes the core platform - higher tiers add history
            allowance, concurrency and scale.
          </p>
        </div>
        <plan-comparison-table :plans="plans" />
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           5. TRUST & REASSURANCE (no testimonials - honest signals only)
           id="everything" lives here rather than on §3 so the scroll cue
           always lands on real content, whether or not §3/§4 render. An app
           with its own signals shows them in the hero strip instead, and the
           cue lands on the FAQ.
           ───────────────────────────────────────────────────────────── -->
      <div v-if="!hasAppTrustSignals" id="everything" class="subscribe-section">
        <v-row justify="center">
          <v-col
            v-for="t in trustSignals"
            :key="t.label"
            cols="6"
            sm="4"
            md="2"
            class="text-center"
          >
            <v-icon color="primary">{{ t.icon }}</v-icon>
            <div class="trust-signal-label">{{ t.label }}</div>
          </v-col>
        </v-row>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           6. OBJECTION-HANDLING MINI-FAQ (approved copy, condensed)
           ───────────────────────────────────────────────────────────── -->
      <div :id="hasAppTrustSignals ? 'everything' : null" class="subscribe-section">
        <div class="text-center mb-6">
          <h2 class="section-h2">Questions before you subscribe?</h2>
        </div>
        <v-expansion-panels accordion flat class="subscribe-faq mx-auto">
          <v-expansion-panel v-for="(f, i) in faqs" :key="i">
            <v-expansion-panel-header class="faq-q">{{
              f.q
            }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="faq-a">{{ f.a }}</div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="text-center mt-4">
          <router-link to="/faq" class="subscribe-text-link"
            >See all FAQs</router-link
          >
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────────────────
           7. FINAL CTA + LEGAL
           ───────────────────────────────────────────────────────────── -->
      <div class="subscribe-section text-center">
        <h2 class="section-h2 mb-4">{{ finalCtaTitle }}</h2>
        <v-btn color="primary" large depressed @click="scrollToId('plans')">
          Choose your plan
          <v-icon right>mdi-arrow-up</v-icon>
        </v-btn>
        <!-- [TODO: legal review] -->
        <p class="caption grey--text mt-8 mb-1">{{ resultsDisclaimer }}</p>
        <p v-if="guaranteeInfo" class="caption grey--text mb-1">
          {{ guaranteeInfo.short }}. {{ guaranteeInfo.long }}
        </p>
        <p class="caption grey--text mb-0">
          <a
            :href="refundPolicyUrl"
            target="_blank"
            rel="noopener"
            class="subscribe-text-link"
            >Refund Policy</a
          >
        </p>
      </div>
    </v-container>

    <!-- Demo video modal -->
    <v-dialog
      v-model="showVideoModal"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      width="90%"
      max-width="1600"
      content-class="subscribe-video-dialog"
    >
      <v-card class="demo-modal-card">
        <div class="demo-modal-header">
          <div class="demo-modal-title">See {{ $config.productName }} in action</div>
          <v-btn icon class="demo-modal-close" @click="showVideoModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="demo-modal-video-wrap">
          <video
            v-if="demoVideo"
            ref="demoVideo"
            class="demo-modal-video"
            :src="demoVideo.src"
            muted
            playsinline
            controls
            preload="metadata"
          ></video>
        </div>
      </v-card>
    </v-dialog>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="5000">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Redirect-to-Stripe overlay -->
    <v-overlay :value="busy" opacity="0.92" z-index="2000">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="primary"
          class="mb-4"
        />
        <div class="text-h6 font-weight-medium">
          Taking you to secure checkout…
        </div>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { formatPlanName } from "@shared/utils/planLabels";
import SubscriptionPlanCard from "@shared/components/SubscriptionPlanCard.vue";
import PlanComparisonTable from "@shared/components/PlanComparisonTable.vue";
import { safeInternalPath } from "@shared/utils/safePath";
import { isMember, registerFirst, serverMessage } from "@shared/utils/memberFunnel";
import config from "@/config";
import { guarantee, refundPolicyUrl } from "@shared/utils/guarantee";
import { resultsDisclaimer, dataSecurityAnswer, creditsExplainer, creditsEnabled } from "@shared/utils/productWording";

// The guarantee question leads the mini-FAQ on apps that offer one.
function guaranteeFaq() {
  var value = guarantee();
  if (!value) return [];
  return [{ q: value.question || "Is there a money-back guarantee?", a: value.answer || value.long }];
}

export default {
  name: "SubscribeView",
  components: {
    SubscriptionPlanCard,
    PlanComparisonTable
  },
  data() {
    return {
      busy: false,
      errorSnackbar: false,
      errorMessage: "",
      // Demo video. The thumbnail beside the plan cards opens a fullscreen
      // modal that plays the demo recording - keeps the video out of the
      // above-the-fold layout.
      showVideoModal: false,
      // Per-tier persuasion metadata (keyed by lowercased plan name). Stable
      // copy only; numeric limits stay derived in the card / comparison table.
      tierMeta: {
        basic: { tagline: "Get started running jobs." },
        pro: {
          tagline: "For power users shipping jobs regularly.",
          recommended: true
        },
        ultra: { tagline: "Maximum allowance and concurrency." }
      },
      capabilities: [
        {
          icon: "mdi-file-upload-outline",
          title: "Input in, result out",
          desc: "Upload your file and get a finished result back - no extra tooling needed."
        },
        {
          icon: "mdi-account-star-outline",
          title: "Consistent across the whole job",
          desc: "The same look and quality carried through every part of the result."
        },
        {
          icon: "mdi-script-text-outline",
          title: "Tailored to your input, not a template",
          desc: "The system works from your specific input, not a generic preset."
        },
        {
          icon: "mdi-timer-outline",
          title: "Runs when you need it",
          desc: "Jobs process automatically once submitted, so you get results without manual steps."
        },
        {
          icon: "mdi-check-decagram-outline",
          title: "Checked before you see it",
          desc: "Every output is reviewed automatically and regenerated when it does not meet the bar."
        },
        {
          icon: "mdi-palette-outline",
          title: "Direct the result",
          desc: "Steer the output and regenerate any part you want to take in a different direction."
        },
        {
          icon: "mdi-robot-outline",
          title: "Bring your own AI agent",
          desc: "Drive it from Claude Code, Codex or Cursor over MCP - or build on the public API."
        }
      ],
      // The reference grid below the fold. Cancelling, changing plans and
      // Stripe checkout are already in the hero strip, so they are not repeated.
      trustSignals: [
        {
          icon: "mdi-shield-check-outline",
          label: "Your input, your job, your result"
        },
        { icon: "mdi-account-lock-outline", label: "Private to your account" },
        { icon: "mdi-lock-outline", label: "Encrypted connections (HTTPS)" },
        { icon: "mdi-eye-outline", label: "Review every result before export" }
      ],
      // Generic product FAQ copy. Stripe-safe; credits framed as
      // usage units, not money.
      resultsDisclaimer: resultsDisclaimer(),
      faqs: guaranteeFaq().concat(creditsEnabled() ? [
        {
          q: config.CREDITS_FAQ_QUESTION || "What are credits, and do they expire?",
          a: config.PRICING_CREDITS_EXPLAINER
            ? config.PRICING_CREDITS_EXPLAINER
            : creditsExplainer()
        }
      ] : []).concat([
        {
          q: config.TOPUPS_ENABLED === false || !creditsEnabled() ? 'Can I change plans?' : "Can I buy more credits or change plans?",
          a: !creditsEnabled()
            ? "Yes. You can switch plans at any time from your Subscription page."
            : config.TOPUPS_ENABLED === false ? 'You can change plans for a larger monthly allowance. Top-ups are not available.' : "Yes. On any paid plan you can buy additional credits on the Billing page whenever you run out, or upgrade to a higher tier for a larger monthly allocation. Payment is processed securely through Stripe and credits are available immediately."
        },
        {
          q: "What happens if I cancel my subscription?",
          a: config.SUBSCRIBE_CANCEL_FAQ || (creditsEnabled()
            ? "You keep full access until the end of your current billing period, and remaining included credits can be used until then (they don't roll over). Your projects and results are preserved, and you can reactivate at any time."
            : "You keep full access until the end of your current billing period. Your work is preserved, and you can reactivate at any time.")
        },
        {
          q: "Is my data secure?",
          a: dataSecurityAnswer()
        }
      ])
    };
  },
  computed: {
    ...mapGetters([
      "isAuthenticated",
      "purchasablePlans",
      "currentSubscription",
      "currentUser",
      "forcePaidPlans"
    ]),
    plans() {
      // purchasablePlans already strips the Free plan under force-paid. If the
      // flag is somehow off, fall back to filtering nothing.
      return this.purchasablePlans;
    },
    // Apps may name their own starting plan with an honest label. Only then is
    // ?plan= honoured: reference apps already send it for other reasons.
    recommendedPlanName() {
      var chosen = config.RECOMMENDED_PLAN;
      if (!chosen || !chosen.name) return "";
      var requested =
        typeof this.$route.query.plan === "string"
          ? this.$route.query.plan.toLowerCase()
          : "";
      var offered =
        requested !== "free" &&
        this.plans.some(function(plan) {
          return plan && plan.name && plan.name.toLowerCase() === requested;
        });
      return offered ? requested : String(chosen.name).toLowerCase();
    },
    recommendedLabel() {
      var chosen = config.RECOMMENDED_PLAN;
      return chosen && chosen.label ? chosen.label : "Most Popular";
    },
    hasPlanMarketing() {
      return !!(this.$config && this.$config.PLAN_MARKETING);
    },
    heroCopy() {
      var override = this.$config && this.$config.SUBSCRIBE_HERO;
      return {
        eyebrow: (override && override.eyebrow) || "You're in - pick your plan to get started",
        title: (override && override.title) || "Turn your files into finished results",
        subtitle:
          (override && override.subtitle) ||
          "Upload your input. " +
            this.$config.productName +
            " runs the job, checks the output and delivers the finished result."
      };
    },
    hasAppTrustSignals() {
      var signals = this.$config && this.$config.SUBSCRIBE_TRUST_SIGNALS;
      return Array.isArray(signals) && signals.length > 0;
    },
    // The hero strip: the app's own signals, or the generic three. The app's
    // guarantee leads either way unless its own list already names it.
    heroTrustItems() {
      var items = this.hasAppTrustSignals
        ? this.$config.SUBSCRIBE_TRUST_SIGNALS.map(function (signal) {
            return { icon: signal.icon, label: signal.label, color: "primary" };
          })
        : [
            { icon: "mdi-check-decagram", label: "Cancel anytime", color: "success" },
            { icon: "mdi-swap-horizontal", label: "Change plans whenever", color: "warning" },
            { icon: "mdi-lock", label: "Secure checkout via Stripe", color: "info" }
          ];
      var offer = this.guaranteeInfo;
      var named = offer && items.some(function (item) {
        return String(item.label).toLowerCase() === String(offer.short).toLowerCase();
      });
      if (offer && !named) {
        items.unshift({ icon: "mdi-shield-check", label: offer.short, color: "success" });
      }
      return items;
    },
    finalCtaTitle() {
      return (this.$config && this.$config.SUBSCRIBE_FINAL_CTA_TITLE) || "Ready to run your first job?";
    },
    currentPlan() {
      return this.currentSubscription &&
        this.currentSubscription.subscription_plan
        ? this.currentSubscription.subscription_plan
        : "";
    },
    paymentFailed() {
      var status = this.currentSubscription && this.currentSubscription.subscription_status;
      return status === "past_due" || status === "unpaid";
    },
    userName() {
      return this.currentUser && this.currentUser.name
        ? this.currentUser.name
        : "";
    },
    refundPolicyUrl() {
      return refundPolicyUrl();
    },
    guaranteeInfo() {
      return guarantee();
    },
    // The paid-funnel demo ($config.PAID_FUNNEL_VIDEO = {src, poster,
    // subtitle, points}); an app without a recording sets it to null.
    demoVideo() {
      var custom = config.PAID_FUNNEL_VIDEO;
      if (custom === null) return null;
      return Object.assign(
        {
          src: "/videos/demo-1.mp4",
          poster: "/videos/demo-poster.jpg",
          subtitle: "Take the 2-minute tour before you choose a plan.",
          points: [
            "Your input become a finished job, step by step",
            "A finished result, start to finish",
            "Every output checked and regenerated if flawed"
          ]
        },
        custom || {}
      );
    },
    showDemoVideo() {
      return this.forcePaidPlans && !!this.demoVideo;
    }
  },
  watch: {
    showVideoModal(val) {
      if (val) {
        // Play from the start each time the modal opens; pause when it closes.
        this.$nextTick(this.playDemo);
      } else {
        this.pauseDemo();
      }
    }
  },
  methods: {
    ...mapActions(["fetchPlans", "fetchSubscription", "createCheckoutSession"]),
    tierMetaFor(plan) {
      var key = plan && plan.name ? plan.name.toLowerCase() : "";
      return this.tierMeta[key] || {};
    },
    tierTagline(plan) {
      return this.tierMetaFor(plan).tagline || "";
    },
    isRecommended(plan) {
      if (this.recommendedPlanName) {
        return !!(
          plan &&
          plan.name &&
          plan.name.toLowerCase() === this.recommendedPlanName
        );
      }
      return this.tierMetaFor(plan).recommended === true;
    },
    previousTierName(index) {
      if (index <= 0 || !this.plans[index - 1]) return "";
      return formatPlanName(this.plans[index - 1].name);
    },
    scrollToId(id) {
      var el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    playDemo() {
      var v = this.$refs.demoVideo;
      if (!v) return;
      v.muted = true;
      try {
        v.currentTime = 0;
      } catch (e) {
        // currentTime can throw before metadata loads; ignore.
      }
      var p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(function () {});
      }
    },
    pauseDemo() {
      var v = this.$refs.demoVideo;
      if (v) {
        v.pause();
      }
    },
    startCheckout(plan) {
      if (!plan) return;
      // Signed-out visitors and anonymous trial guests cannot subscribe (the
      // backend refuses a recurring charge on a disposable, fingerprint-keyed
      // account). Send them to register first, carrying the chosen plan.
      if (!isMember(this.$store)) {
        this.$router.push(registerFirst("/subscribe", plan.name)).catch(() => {});
        return;
      }

      // After payment, return to the subscription page which already handles the
      // post-checkout success state. Cancelling returns here to the gate. An
      // optional same-app ?next= path rides along so the user can get back.
      const next = safeInternalPath(this.$route.query.next);
      const returnQuery = next ? "next=" + encodeURIComponent(next) : "";
      const successUrl = `${window.location.origin}/subscription?success=true${returnQuery ? "&" + returnQuery : ""}`;
      const cancelUrl = `${window.location.origin}/subscribe${returnQuery ? "?" + returnQuery : ""}`;

      this.busy = true;
      this.createCheckoutSession({
        planId: plan.id,
        billingCycle: "monthly",
        successUrl,
        cancelUrl
      })
        .then(response => {
          if (response && response.checkout_url) {
            // The subscription page confirms the change by this plan name.
            sessionStorage.setItem("pendingSubscriptionPlanName", plan.name);
            window.location.href = response.checkout_url;
            return; // keep overlay up during navigation
          }
          // No checkout URL means the subscription was changed server-side
          // (e.g. an existing customer). Refresh so the gate releases.
          this.fetchSubscription();
          this.busy = false;
        })
        .catch(error => {
          console.error("Error creating checkout session:", error);
          this.errorMessage = serverMessage(error, "Failed to start checkout. Please try again.");
          this.errorSnackbar = true;
          this.busy = false;
        });
    },
    logout() {
      this.$store.dispatch("logout");
      this.$router.push("/login");
    }
  },
  async created() {
    await this.fetchPlans();
    if (this.isAuthenticated) {
      await this.fetchSubscription();
    }
  }
};
</script>

<style scoped>
.subscribe-view {
  padding-bottom: 2rem;
}

/* ── 1. Compact hero (kept short so cards stay above the fold) ── */
.subscribe-hero {
  padding: 2px 0 8px;
}

.subscribe-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-cyan, #0ea5e9);
  margin-bottom: 2px;
}

.subscribe-title {
  font-size: 1.6rem;
  line-height: 1.15;
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 4px;
}

.subscribe-subtitle {
  font-size: 0.9rem;
  color: var(--text-secondary);
  max-width: 680px;
  margin: 0 auto 6px;
  line-height: 1.45;
}

.subscribe-trust-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.subscribe-trust-strip .trust-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.subscribe-trust-strip .trust-dot {
  color: var(--text-muted);
}

/* Phones: the items wrap one or two to a line, so a gap separates them and
   the dots (which would dangle at line ends) go. */
@media (max-width: 600px) {
  .subscribe-trust-strip {
    gap: 4px 14px;
  }
  .subscribe-trust-strip .trust-dot {
    display: none;
  }
}

.subscribe-signed-in {
  font-size: 0.78rem;
  color: var(--text-secondary);
  margin-top: 4px;
  margin-bottom: 0;
}

.subscribe-text-link {
  color: var(--text-cyan-bright);
  text-decoration: none;
}

.subscribe-text-link:hover {
  text-decoration: underline;
}

/* ── 2. Plan cards ── */
.plans-row {
  margin-top: 2px;
}

/* Demo video panel beside the plan cards */
.demo-video-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 18px;
  background: linear-gradient(
    170deg,
    var(--light-bg-selected, #eff6ff) 0%,
    var(--bg-secondary, #f8fafc) 55%,
    var(--bg-card, #ffffff) 100%
  );
  border: 1px solid var(--border-cyan-20, rgba(56, 189, 248, 0.25));
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.demo-video-panel:hover {
  border-color: var(--text-blue-light, #3b82f6);
  transform: translateY(-3px);
  box-shadow: 0 6px 18px var(--shadow-sm, rgba(15, 23, 42, 0.08));
}

.demo-video-panel:focus-visible {
  outline: 2px solid var(--text-blue-light, #3b82f6);
  outline-offset: 2px;
}

.demo-video-thumb {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.demo-video-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.demo-video-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.28);
  transition: background 0.2s ease;
}

.demo-video-panel:hover .demo-video-overlay {
  background: rgba(2, 6, 23, 0.42);
}

.demo-video-play-circle {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: var(--text-blue-light, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.5);
}

.demo-video-title {
  font-weight: 700;
  color: var(--text-heading);
  font-size: 1.05rem;
}

.demo-video-sub {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.4;
  margin-top: 2px;
}

.demo-video-header {
  text-align: center;
}

.demo-video-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-blue-light, #3b82f6);
  margin-bottom: 4px;
}

.demo-video-footer {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.demo-video-points-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-heading);
  margin-bottom: 2px;
}

.demo-video-point {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.82rem;
  line-height: 1.35;
  color: var(--text-secondary);
}

.demo-video-watch {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-blue-light, #3b82f6);
}

.subscribe-scroll-cue {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-blue-light, #3b82f6);
  text-decoration: none;
}

.subscribe-scroll-cue:hover {
  text-decoration: underline;
}

/* ── Below-the-fold sections ── */
.subscribe-section {
  margin-top: 72px;
}

.section-h2 {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text-heading);
  margin-bottom: 4px;
}

.section-sub {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 0;
}

/* ── 3. Capability showcase ── */
.capability-card {
  height: 100%;
  padding: 20px;
  background: var(--bg-card);
  border: 1px solid var(--toolbar-border, #cbd5e1);
  border-radius: 12px;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.capability-card:hover {
  border-color: var(--text-blue-light, #3b82f6);
  transform: translateY(-3px);
}

.capability-title {
  font-weight: 600;
  color: var(--text-heading);
  margin-top: 10px;
}

.capability-desc {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: 4px;
  line-height: 1.5;
}

/* ── 5. Trust signals ── */
.trust-signal-label {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 6px;
  line-height: 1.3;
}

.invite-note {
  margin-top: 28px;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

/* ── 6. FAQ ── */
.subscribe-faq {
  max-width: 760px;
}

.faq-q {
  font-weight: 600;
  color: var(--text-primary);
}

.faq-a {
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.6;
}

@media (max-width: 600px) {
  .subscribe-title {
    font-size: 1.5rem;
  }

  .subscribe-section {
    margin-top: 48px;
  }
}
</style>

<style>
/* Demo video modal. v-dialog content is portaled to <body>, so these live in a
   non-scoped block but are namespaced under the dialog's content-class so
   nothing leaks globally. */
.subscribe-video-dialog {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
}

.subscribe-video-dialog .demo-modal-card {
  background: #0b1220 !important;
  border-radius: 16px !important;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Mobile: fill the screen (mirrors the smAndDown fullscreen on the dialog). */
@media (max-width: 959px) {
  .subscribe-video-dialog .demo-modal-card {
    border-radius: 0 !important;
    height: 100vh;
  }
}

.subscribe-video-dialog .demo-modal-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.subscribe-video-dialog .demo-modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
}

.subscribe-video-dialog .demo-modal-close {
  color: #cbd5e1 !important;
}

.subscribe-video-dialog .demo-modal-video-wrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.subscribe-video-dialog .demo-modal-video {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  background: #000;
}
</style>
